/**
 * Sidebar widget that lets the teacher switch between (batch, subject) contexts.
 * Renders only when teacher has more than one context (TeacherLayout enforces).
 *
 * Groups by batch so multi-subject teachers see:
 *   Sharma Class 12
 *     Chemistry  ←
 *     Physics
 *   Sharma Class 11
 *     Chemistry
 *
 * Phase E.6 will polish this; for E.1 we ship a minimal working version so
 * the layout can render.
 */

import { useNavigate } from 'react-router-dom';

const SUBJECT_LABEL = { physics: 'Physics', chemistry: 'Chemistry', mathematics: 'Maths' };

export default function ContextPicker({ contexts, currentBatchId, currentSubject, teacherId }) {
  const navigate = useNavigate();

  // Group by batch_id, preserving the most-recently-active order from the API
  const byBatch = new Map();
  for (const c of contexts) {
    if (!byBatch.has(c.batch_id)) {
      byBatch.set(c.batch_id, { batch_name: c.batch_name, classes: c.classes, subjects: [] });
    }
    byBatch.get(c.batch_id).subjects.push(c.subject);
  }

  return (
    <div className="px-5 py-3 border-b border-white/10">
      <div className="text-[10px] uppercase tracking-wider text-white/35 mb-2">
        Switch context
      </div>
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {[...byBatch.entries()].map(([batchId, info]) => (
          <div key={batchId}>
            <div className="text-[11px] text-white/50 truncate">{info.batch_name}</div>
            <div className="mt-1 space-y-0.5">
              {info.subjects.map(subject => {
                const active = batchId === currentBatchId && subject === currentSubject;
                return (
                  <button
                    key={subject}
                    onClick={() => navigate(`/teachers/${teacherId}/batches/${batchId}/${subject}`)}
                    className={`block w-full text-left text-xs px-2 py-1 rounded transition-colors ${
                      active
                        ? 'bg-white/10 text-white font-medium'
                        : 'text-white/55 hover:bg-white/5 hover:text-white/80'
                    }`}
                  >
                    {SUBJECT_LABEL[subject] || subject}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
